import { useState, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { signIn } from "@auth/services/signin";
import { navigateTo } from "@jkast90/kast-common/auth";
import { loginRedirect } from "@common/redirect";
import { LegalLinks } from "@jkast90/kast-common/legal/react";
import { Button, Card, TextField } from "@jkast90/kast-common/ui";
import styles from "./SignUp.module.css"; // reuse same styles

export default function SignIn() {
  const [searchParams] = useSearchParams();
  const redirect = loginRedirect(searchParams.get("redirect"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const tokens = await signIn(email, password);
      // Redirect back to the app with tokens as hash params
      const params = new URLSearchParams({
        access_token: tokens.accessToken,
        id_token: tokens.idToken,
        refresh_token: tokens.refreshToken,
      });
      navigateTo(`${redirect}#${params.toString()}`);
    } catch (err) {
      const msg = (err instanceof Error && err.message) || "Sign in failed.";
      if (msg.includes("User is not confirmed")) {
        setError("Your email hasn't been verified yet. Check your inbox for a confirmation link.");
      } else if (msg.includes("Incorrect username or password")) {
        setError("Incorrect email or password.");
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <Card padding="lg" className={styles.card}>
        <img src="/kd_logo.png" alt="Kast Dev" className={styles.logo} />
        <h1 className={styles.title}>Sign in to Kast Dev</h1>
        <p className={styles.subtitle}>Welcome back</p>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="kui-form">
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />

          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Your password"
          />

          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            loadingLabel="Signing in..."
            className={styles.button}
          >
            Sign In
          </Button>
        </form>

        <p className={styles.loginLink}>
          Don't have an account? <a href={`/signup?redirect=${encodeURIComponent(redirect)}`}>Sign up</a>
        </p>

        <LegalLinks className={styles.legalLinks} />
      </Card>
    </div>
  );
}
